import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { Button, Form, Spinner, Badge } from "react-bootstrap";
import { FaDownload, FaChevronDown, FaChevronRight, FaFilter, FaCaretDown } from "react-icons/fa";

import "./SiteVisitReport.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatWorkHour(h) {
  if (!h || h === 0) return "-";
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}h ${mins > 0 ? mins + "m" : ""}`.trim();
}


export default function SiteVisitReport() {
  const currentYear = new Date().getFullYear();
  const [selectedYears, setSelectedYears] = useState(new Set([currentYear]));
  const [yearDropOpen, setYearDropOpen] = useState(false);
  const yearDropRef = useRef(null);
  const [allRecords, setAllRecords] = useState([]); // raw data from API
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [selectedSites, setSelectedSites] = useState(new Set()); // empty = all
  const [siteDropOpen, setSiteDropOpen] = useState(false);
  const [siteSearch, setSiteSearch] = useState("");
  const siteDropRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("pivot"); // "pivot" | "grid"
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [pivotValue, setPivotValue] = useState("days"); // "days" | "hours"

  // Fetch all records for the selected years (no employee filter on API — filter locally)
  const fetchData = useCallback(() => {
    if (selectedYears.size === 0) return;
    setLoading(true);
    setError(null);
    setSelectedEmployee("all");
    setSelectedSites(new Set());

    const yearsParam = [...selectedYears].sort().join(",");
    axios.get(`${API_URL}/api/reports/site-visit`, { params: { years: yearsParam } })
      .then((res) => {
        const data = res.data.records || [];
        setAllRecords(data);
        const expanded = {};
        [...new Set(data.map((r) => r.employee_name))].forEach((n) => { expanded[n] = false; });
        setExpandedEmployees(expanded);
      })
      .catch(() => setError("Failed to load site visit data."))
      .finally(() => setLoading(false));
  }, [selectedYears]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (siteDropRef.current && !siteDropRef.current.contains(e.target)) {
        setSiteDropOpen(false);
        setSiteSearch("");
      }
      if (yearDropRef.current && !yearDropRef.current.contains(e.target))
        setYearDropOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Derive unique employees and sites from the raw data (only those present)
  const employeeOptions = useMemo(() =>
    [...new Set(allRecords.map((r) => r.employee_name))].sort(), [allRecords]);

  const siteOptions = useMemo(() => {
    const base = allRecords;
    // If an employee is selected, only show sites for that employee
    const filtered = selectedEmployee !== "all"
      ? base.filter((r) => r.employee_name === selectedEmployee)
      : base;
    return [...new Set(filtered.map((r) => r.location || "Unknown"))].sort();
  }, [allRecords, selectedEmployee]);

  // Active filtered records used for pivot + grid
  const records = useMemo(() => {
    return allRecords.filter((r) => {
      if (selectedEmployee !== "all" && r.employee_name !== selectedEmployee) return false;
      if (selectedSites.size > 0 && !selectedSites.has(r.location || "Unknown")) return false;
      return true;
    });
  }, [allRecords, selectedEmployee, selectedSites]);

  // Build pivot data structure — tracks both days count and hours sum per month
  const pivotData = useMemo(() => {
    const empMap = {};

    records.forEach((r) => {
      const emp = r.employee_name;
      const site = r.location || "Unknown";
      const m = r.month - 1; // 0-based

      if (!empMap[emp]) empMap[emp] = { sites: {} };
      if (!empMap[emp].sites[site]) {
        empMap[emp].sites[site] = { days: Array(12).fill(0), hours: Array(12).fill(0) };
      }
      empMap[emp].sites[site].days[m]++;
      empMap[emp].sites[site].hours[m] += r.work_hour || 0;
    });

    return Object.entries(empMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([empName, { sites }]) => {
        const siteList = Object.entries(sites)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([siteName, { days, hours }]) => ({
            siteName,
            days,
            hours,
            totalDays: days.reduce((s, c) => s + c, 0),
            totalHours: hours.reduce((s, c) => s + c, 0),
          }));

        const monthDays = Array(12).fill(0);
        const monthHours = Array(12).fill(0);
        siteList.forEach(({ days, hours }) => {
          days.forEach((c, i) => { monthDays[i] += c; });
          hours.forEach((h, i) => { monthHours[i] += h; });
        });

        return {
          empName,
          sites: siteList,
          monthDays,
          monthHours,
          totalDays: monthDays.reduce((s, c) => s + c, 0),
          totalHours: monthHours.reduce((s, h) => s + h, 0),
        };
      });
  }, [records]);

  // Grand column totals
  const colDays = useMemo(() => { const t = Array(12).fill(0); pivotData.forEach(({ monthDays }) => monthDays.forEach((c, i) => { t[i] += c; })); return t; }, [pivotData]);
  const colHours = useMemo(() => { const t = Array(12).fill(0); pivotData.forEach(({ monthHours }) => monthHours.forEach((h, i) => { t[i] += h; })); return t; }, [pivotData]);

  const grandDays = colDays.reduce((s, c) => s + c, 0);
  const grandHours = colHours.reduce((s, h) => s + h, 0);

  // Helper: format cell value based on current selection
  const fmt = (days, hours) =>
    pivotValue === "hours" ? (hours > 0 ? formatWorkHour(hours) : "-") : (days > 0 ? days : null);

  const toggleEmployee = (empName) => {
    setExpandedEmployees((prev) => ({ ...prev, [empName]: !prev[empName] }));
  };

  // ── CSV Export ────────────────────────────────────────────
  const downloadCSV = () => {
    const headers = ["Date", "Employee", "Site / Location", "Clock-in", "Clock-out", "Work Hours", "Day Count"];
    const rows = records.map((r) => [
      r.date,
      r.employee_name,
      r.location,
      r.in_time || "",
      r.out_time || "",
      r.work_hour ? r.work_hour.toFixed(2) : "0.00",
      r.day_status === "Full Day" ? 1 : r.day_status === "Half Day" ? 0.5 : r.day_status,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `site_visit_report_${[...selectedYears].sort().join("_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 5; y--) yearOptions.push(y);

  return (
    <div className="svr-container">


      {/* ── Filters Bar ── */}
      <div className="svr-filter-row">

        <div className="svr-filters-left">
          <div className="svr-filter-group" ref={yearDropRef} style={{ position: "relative" }}>
            {/* <FaFilter className="svr-filter-icon" /> */}
            <span className="svr-filter-label">Year</span>
            <button
              className="svr-multisel-btn"
              onClick={() => setYearDropOpen((o) => !o)}
            >
              <span className="svr-multisel-label">
                {selectedYears.size === 0
                  ? "Select Year"
                  : selectedYears.size === 1
                    ? [...selectedYears][0]
                    : `${[...selectedYears].sort().join(", ")}`}
              </span>
              <FaCaretDown size={11} />
            </button>

            {yearDropOpen && (
              <div className="svr-multisel-dropdown">
                <div className="svr-multisel-actions">
                  <button onClick={() => setSelectedYears(new Set(yearOptions))}>All</button>
                  <button onClick={() => setSelectedYears(new Set([currentYear]))}>Reset</button>
                </div>
                <div className="svr-multisel-list">
                  {yearOptions.map((y) => (
                    <label key={y} className="svr-multisel-item">
                      <input
                        type="checkbox"
                        checked={selectedYears.has(y)}
                        onChange={() => {
                          setSelectedYears((prev) => {
                            const next = new Set(prev);
                            next.has(y) ? next.delete(y) : next.add(y);
                            return next;
                          });
                        }}
                      />
                      <span>{y}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="svr-filter-group">
            <span className="svr-filter-label">Employee</span>
            <Form.Select
              size="sm"
              value={selectedEmployee}
              onChange={(e) => { setSelectedEmployee(e.target.value); setSelectedSites(new Set()); }}
              className="svr-select"
              disabled={employeeOptions.length === 0}
            >
              <option value="all">All Employees</option>
              {employeeOptions.map((name) => <option key={name} value={name}>{name}</option>)}
            </Form.Select>
          </div>

          {/* ── Site multi-select ── */}
          <div className="svr-filter-group" ref={siteDropRef} style={{ position: "relative" }}>
            <span className="svr-filter-label">Site</span>
            <button
              className="svr-multisel-btn"
              onClick={() => { setSiteDropOpen((o) => { if (o) setSiteSearch(""); return !o; }); }}
              disabled={siteOptions.length === 0}
            >
              <span className="svr-multisel-label">
                {selectedSites.size === 0
                  ? "All Sites"
                  : selectedSites.size === 1
                    ? [...selectedSites][0]
                    : `${selectedSites.size} sites selected`}
              </span>
              <FaCaretDown size={11} />
            </button>

            {siteDropOpen && (
              <div className="svr-multisel-dropdown">
                {/* Search input */}
                <div className="svr-multisel-search">
                  <input
                    type="text"
                    placeholder="Search site..."
                    value={siteSearch}
                    onChange={(e) => setSiteSearch(e.target.value)}
                    className="svr-multisel-search-input"
                    autoFocus
                  />
                </div>
                {/* Select all / Clear */}
                <div className="svr-multisel-actions">
                  <button onClick={() => setSelectedSites(new Set(siteOptions))}>All</button>
                  <button onClick={() => setSelectedSites(new Set())}>Clear</button>
                </div>
                <div className="svr-multisel-list">
                  {siteOptions
                    .filter((site) => site.toLowerCase().includes(siteSearch.toLowerCase()))
                    .map((site) => (
                      <label key={site} className="svr-multisel-item">
                        <input
                          type="checkbox"
                          checked={selectedSites.has(site)}
                          onChange={() => {
                            setSelectedSites((prev) => {
                              const next = new Set(prev);
                              next.has(site) ? next.delete(site) : next.add(site);
                              return next;
                            });
                          }}
                        />
                        <span>{site}</span>
                      </label>
                    ))}
                </div>
              </div>
            )}
          </div>


          <div className="svr-filter-group">
            <span className="svr-filter-label">View</span>
            <div className="svr-view-toggle">
              <button className={`svr-toggle-btn${viewMode === "pivot" ? " active" : ""}`} onClick={() => setViewMode("pivot")}>Pivot</button>
              <button className={`svr-toggle-btn${viewMode === "grid" ? " active" : ""}`} onClick={() => setViewMode("grid")}>Grid</button>
            </div>
          </div>

          {viewMode === "pivot" && (
            <div className="svr-filter-group">
              <span className="svr-filter-label">Values</span>
              <div className="svr-view-toggle">
                <button className={`svr-toggle-btn${pivotValue === "days" ? " active" : ""}`} onClick={() => setPivotValue("days")}>Days</button>
                <button className={`svr-toggle-btn${pivotValue === "hours" ? " active" : ""}`} onClick={() => setPivotValue("hours")}>Hours</button>
              </div>
            </div>
          )}
        </div>
        <div className="svr-filters-right">
          <Button
            variant="primary"
            size="sm"
            className="svr-csv-btn"
            onClick={downloadCSV}
            disabled={loading || records.length === 0}
          >
            <FaDownload className="me-1" /> Download CSV
          </Button>
        </div>
      </div>



      {/* ── Loading / Error ── */}
      {loading && (
        <div className="svr-loading">
          <Spinner animation="border" size="sm" className="me-2" /> Loading…
        </div>
      )}
      {error && <div className="svr-error">{error}</div>}

      {/* ── Pivot Table ── */}
      {!loading && !error && viewMode === "pivot" && (
        <div className="svr-table-wrap">
          {pivotData.length === 0 ? (
            <div className="svr-empty">No site visit records found for {[...selectedYears].sort().join(", ")}.</div>
          ) : (
            <table className="svr-table">
              <thead>
                <tr>
                  <th className="svr-th-emp">Employee ({[...new Set(records.map((r) => r.employee_name))].length || ''}) / Site ({records.length || ''})</th>
                  {MONTHS.map((m) => <th key={m} className="svr-th-month">{m}</th>)}
                  <th className="svr-th-total">{pivotValue === "hours" ? "Total Hrs" : "Total Days"}</th>
                </tr>
              </thead>
              <tbody>
                {pivotData.map(({ empName, sites, monthDays, monthHours, totalDays, totalHours }) => (
                  <React.Fragment key={empName}>
                    {/* Employee row */}
                    <tr className="svr-row-emp" onClick={() => toggleEmployee(empName)}>
                      <td className="svr-td-emp">
                        <span className="svr-expand-icon">
                          {expandedEmployees[empName] ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                        </span>
                        {empName}
                      </td>
                      {monthDays.map((_, i) => {
                        const val = fmt(monthDays[i], monthHours[i]);
                        return <td key={i} className="svr-td-month">{val ?? <span className="svr-zero">-</span>}</td>;
                      })}
                      <td className="svr-td-total">
                        {pivotValue === "hours" ? formatWorkHour(totalHours) : totalDays}
                      </td>
                    </tr>

                    {/* Site rows */}
                    {expandedEmployees[empName] && sites.map(({ siteName, days, hours, totalDays: sd, totalHours: sh }) => (
                      <tr key={`${empName}-${siteName}`} className="svr-row-site">
                        <td className="svr-td-site">
                          <span className="svr-site-dot">●</span>
                          {siteName}
                        </td>
                        {days.map((_, i) => {
                          const val = fmt(days[i], hours[i]);
                          return <td key={i} className="svr-td-month">{val ?? <span className="svr-zero">-</span>}</td>;
                        })}
                        <td className="svr-td-total">
                          {pivotValue === "hours" ? formatWorkHour(sh) : sd}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}

                {/* Grand total row — sticky bottom */}
                <tr className="svr-row-grand-total">
                  <td className="svr-td-emp">Grand Total</td>
                  {colDays.map((_, i) => {
                    const val = fmt(colDays[i], colHours[i]);
                    return <td key={i} className="svr-td-month">{val ?? <span className="svr-zero">-</span>}</td>;
                  })}
                  <td className="svr-td-total">
                    {pivotValue === "hours" ? formatWorkHour(grandHours) : grandDays}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Grid View ── */}
      {!loading && !error && viewMode === "grid" && (
        <div className="svr-table-wrap">
          {records.length === 0 ? (
            <div className="svr-empty">No site visit records found for {[...selectedYears].sort().join(", ")}.</div>
          ) : (
            <table className="svr-table svr-grid-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee</th>
                  <th>Site / Location</th>
                  <th>Clock-in</th>
                  <th>Clock-out</th>
                  <th>Work Hours</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, idx) => (
                  <tr key={idx} className={r.has_worked ? "svr-row-worked" : "svr-row-absent"}>
                    <td className="svr-td-date">{r.date}</td>
                    <td className="svr-td-name">{r.employee_name}</td>
                    <td className="svr-td-loc">{r.location || "-"}</td>
                    <td className="svr-td-time">{r.in_time ? r.in_time.split(" ")[1] || r.in_time : "-"}</td>
                    <td className="svr-td-time">{r.out_time ? r.out_time.split(" ")[1] || r.out_time : "-"}</td>
                    <td className="svr-td-hours">{formatWorkHour(r.work_hour)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
